import React from "react";
import { Helmet } from "react-helmet";
import "./terms.css";

const PrivacyPolicy = () => {
  return (
    <div className="terms-container">
      <Helmet>
        <title>Privacy Policy | QuanTech (Canada)</title>
        <meta
          name="description"
          content="Read QuanTech's privacy policy to understand how your data is collected, used, and protected in Canada."
        />
        <meta property="og:title" content="Privacy Policy | QuanTech (Canada)" />
        <meta
          property="og:description"
          content="Learn about QuanTech's privacy practices and how your information is handled in Canada."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.quantech.pro/privacy-policy" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | QuanTech (Canada)" />
        <meta
          name="twitter:description"
          content="Learn about QuanTech's privacy practices and how your information is handled in Canada."
        />
      </Helmet>

      <center>
        <h1 style={{ textDecoration: "underline", textDecorationColor: "#b9ff66" }}>
          Privacy Policy
        </h1>
      </center>

      <br />
      <br />

      <p>
        At <strong>QuanTech</strong> we respect your privacy and are committed to
        handling personal information responsibly. This Privacy Policy describes how
        we collect, use, disclose, and protect personal information in the course of
        our commercial activities in Canada. All references to "we", "us" or "QuanTech"
        mean QuanTech; "you" or "Client" means the individual or organization that
        interacts with our website or Services.
      </p>

      <h2>Scope & Applicable Law</h2>
      <p>
        Where applicable, our practices are designed to comply with the Personal
        Information Protection and Electronic Documents Act (PIPEDA) and related
        guidance from the Office of the Privacy Commissioner of Canada for private-
        sector commercial activities. Provincial privacy laws (for example, Québec’s
        reform known as Law 25) may impose additional requirements for residents of
        those provinces; we will honour such obligations where required.
      </p>

      <h2>Information We Collect</h2>
      <p>
        We collect personal information that you provide voluntarily (for example:
        name, email address, phone number, billing details, business information,
        and any content you submit for a project). We also automatically collect
        technical and usage information (for example: IP address, browser type,
        device identifiers, cookies and analytics data) to operate and improve our
        services.
      </p>

      <h2>How We Use Information</h2>
      <ul className="terms-list">
        <li>To provide and manage projects, invoices and support;</li>
        <li>To communicate about projects, updates, billing and required notices;</li>
        <li>To send marketing or promotional messages only where you have provided valid consent under applicable law (see "Electronic Communications" below);</li>
        <li>To comply with legal obligations and to detect, prevent, or respond to security incidents;</li>
        <li>To improve and personalise the website and Services.</li>
      </ul>

      <h2>Legal Basis & Consent</h2>
      <p>
        For commercial clients and businesses, consent is often implied by entering a
        commercial relationship; for marketing communications, express or implied
        consent rules under Canada’s anti-spam requirements (CASL) apply. Where
        required by law, we will obtain your explicit consent before collecting,
        using, or disclosing personal information for new purposes.
      </p>

      <h2>Electronic Communications & CASL</h2>
      <p>
        If you opt in to receive marketing or promotional messages, we will send
        commercial electronic messages only in compliance with Canada’s Anti-Spam
        Legislation (CASL). Every promotional message will clearly identify the
        sender and include an unsubscribe mechanism. You may withdraw consent (opt
        out) at any time using the unsubscribe link or by contacting us.
      </p>

      <h2>Third-Party Services & Cross-Border Transfers</h2>
      <p>
        We may share information with trusted service providers (e.g., hosting,
        payment processors, analytics) who process data on our behalf. Some of these
        providers may store or process personal information outside Canada (for
        example in the United States or Europe). When personal information is
        transferred outside Canada we take reasonable steps to ensure it receives an
        adequate level of protection (for example contractual safeguards).
      </p>

      <h2>Retention</h2>
      <p>
        We retain personal information only for as long as necessary to fulfil the
        purposes described in this policy, to meet legal or accounting obligations,
        or to resolve disputes. Where retention periods are prescribed by law (e.g.
        tax or accounting rules) we will retain information for the applicable period.
      </p>

      <h2>Access, Correction & Deletion Requests</h2>
      <p>
        You have the right to request access to the personal information we hold
        about you, request corrections to inaccurate information, and ask that we
        delete information in certain circumstances. We will respond to access
        requests in accordance with applicable law and guidance (including timelines
        under PIPEDA) and will advise you of any fees or reasonable limits that may
        apply.
      </p>

      <h2>Security & Breach Notification</h2>
      <p>
        We implement reasonable technical and organisational safeguards to protect
        personal information against loss, theft, or unauthorised access. In the
        event of a breach of security safeguards that creates a real risk of
        significant harm, we will follow applicable legal obligations, which may
        include keeping records of the breach, notifying affected individuals, and
        reporting to the Office of the Privacy Commissioner of Canada (or other
        authority) as required by law.
      </p>

      <h2>Cookies & Similar Technologies</h2>
      <p>
        Our website uses cookies and similar technologies to provide essential site
        functionality, remember preferences, and collect analytics. You can control
        cookie settings through your browser; disabling some cookies may affect site
        functionality.
      </p>

      <h2>Children</h2>
      <p>
        We do not knowingly collect personal information from children for whom we
        do not have parental consent. If you believe we have collected personal
        information from a child without appropriate consent, please contact us so
        we can take corrective steps.
      </p>

      <h2>How to Contact Us / Privacy Officer</h2>
      <p>
        If you have questions, wish to make an access or correction request, or want
        to raise a privacy concern, contact our Privacy Officer at:
        <br />
        Email: <a style={{ textDecorationColor: "#b9ff66", color: "black" }} href="mailto:hello@quantech.pro">hello@quantech.pro</a>
        <br />
        Please include sufficient details (invoice/project number, description of
        requested records) so we can respond efficiently.
      </p>

      <h2>Changes to this Policy</h2>
      <p>
        We may update this Privacy Policy from time to time to reflect changes in
        legal requirements or our practices. Material changes will be posted on this
        page with an updated effective date and, where required, notified to
        customers.
      </p>
    </div>
  );
};

export default PrivacyPolicy;